import { hoverDelay } from "./TooltipProvider.js";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { arrow, autoUpdate, flip, offset, shift, useDelayGroup, useDismiss, useFloating, useFocus, useHover, useInteractions, useRole } from "@floating-ui/react";
//#region src/components/Tooltip/useTooltip.ts
function useTooltip({ open: controlledOpen, disabled = false, onOpenChange, placement = "bottom", isTriggerInteractive, caption, "aria-atomic": ariaAtomic, "aria-live": ariaLive, ...props }) {
	const labelId = useId();
	const captionId = useId();
	const arrowRef = useRef(null);
	const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
	const open = disabled ? false : controlledOpen ?? uncontrolledOpen;
	const setOpen = (open, event, reason) => {
		onOpenChange?.(open, event, reason);
		if (controlledOpen === void 0) setUncontrolledOpen(open);
	};
	const data = useFloating({
		placement,
		open,
		onOpenChange: setOpen,
		whileElementsMounted: autoUpdate,
		middleware: [
			offset(6),
			flip({
				crossAxis: placement.includes("-"),
				fallbackAxisSideDirection: "start",
				padding: 5
			}),
			shift({ padding: 5 }),
			arrow({ element: arrowRef })
		]
	});
	const context = data.context;
	const { delay, initialDelay } = useDelayGroup(context);
	if (initialDelay !== hoverDelay) throw new Error("Tooltips must be wrapped in a global <TooltipProvider>");
	const hover = useHover(context, {
		move: false,
		enabled: controlledOpen === void 0,
		delay: isTriggerInteractive ? delay : {},
		mouseOnly: true
	});
	const focus = useFocus(context, { enabled: controlledOpen === void 0 });
	const pressTimer = useRef(void 0);
	useEffect(() => () => window.clearTimeout(pressTimer.current), []);
	const press = useMemo(() => {
		const onTouchEnd = () => {
			if (pressTimer.current === void 0) pressTimer.current = window.setTimeout(() => {
				setOpen(false);
				pressTimer.current = void 0;
			}, 1500);
			else window.clearTimeout(pressTimer.current);
		};
		return { reference: {
			onTouchStart: () => {
				if (pressTimer.current !== void 0) window.clearTimeout(pressTimer.current);
				pressTimer.current = window.setTimeout(() => {
					setOpen(true);
					pressTimer.current = void 0;
				}, 500);
			},
			onTouchEnd,
			onTouchCancel: onTouchEnd
		} };
	}, []);
	const dismiss = useDismiss(context);
	const purpose = "label" in props ? "label" : "description";
	const role = useRole(context, {
		enabled: purpose === "description",
		role: "tooltip"
	});
	const interactions = useInteractions([
		hover,
		focus,
		press,
		dismiss,
		role,
		useMemo(() => purpose === "label" ? { reference: {
			"aria-labelledby": labelId,
			"aria-describedby": caption ? captionId : void 0
		} } : {}, [
			purpose,
			labelId,
			captionId
		])
	]);
	return useMemo(() => ({
		labelId,
		captionId: caption ? captionId : void 0,
		caption,
		purpose,
		open,
		setOpen,
		tooltipProps: {
			"aria-atomic": ariaAtomic,
			"aria-live": ariaLive
		},
		...interactions,
		...data,
		arrowRef
	}), [
		labelId,
		captionId,
		caption,
		role,
		open,
		setOpen,
		interactions,
		data,
		arrowRef
	]);
}
//#endregion
export { useTooltip };

//# sourceMappingURL=useTooltip.js.map